<template>
  <div class="image-viewer" v-show="isOpen" @click="toggleThumbnails">
    <!-- Close Button -->
    <button class="close-button" @click.stop="closeViewer">
      <svg style="min-width: 24px; width: 24px;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    </button>

    <!-- Main Image -->
    <div 
      class="main-image-container" 
      @click.stop="toggleThumbnails"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    >
      <img 
        :src="images[currentIndex]" 
        :alt="`Image ${currentIndex + 1}`"
        :style="imageStyle"
        @wheel="handleZoom"
        class="main-image"
      />
    </div>

    <!-- Thumbnails -->
    <div class="thumbnails-container" v-if="showThumbnails" @click.stop>
      <div 
        v-for="(image, index) in imagesMini" 
        :key="index"
        class="thumbnail"
        :class="{ active: index === currentIndex }"
        @click="selectImage(index)"
      >
        <img :src="image" :alt="`Thumbnail ${index + 1}`" />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ImageViewer',
  props: {
    images: {
      type: Array,
      required: true,
      default: () => []
    },
    imagesMini: {
      type: Array,
      required: true,
      default: () => []
    },
    initialIndex: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      isOpen: false,
      currentIndex: 0,
      showThumbnails: true,
      scale: 1,
      translateX: 0,
      translateY: 0,
      // Touch handling
      touchStartDistance: 0,
      initialScale: 1,
      lastTouchX: 0,
      lastTouchY: 0
    }
  },
  computed: {
    imageStyle() {
      return {
        transform: `scale(${this.scale}) translate(${this.translateX}px, ${this.translateY}px)`,
      }
    }
  },
  methods: {
    openViewer(index = 0) {
      if(this.images.length === 0) return
      this.showThumbnails = this.imagesMini.length === 0 ? false : true
      this.isOpen = true;
      this.currentIndex = index;
      this.resetZoom();
    },
    closeViewer() {
      this.isOpen = false;
      this.resetZoom();
      this.$emit('close');
    },
    toggleThumbnails() {
      if(this.imagesMini.length === 0) return
      this.showThumbnails = !this.showThumbnails;
    },
    selectImage(index) {
      this.currentIndex = index;
      this.resetZoom();
    },
    handleZoom(event) {
      if (!this.showThumbnails) {
        event.preventDefault();
        const delta = event.deltaY > 0 ? -0.1 : 0.1;
        this.scale = Math.max(1, Math.min(5, this.scale + delta));
        
        if (this.scale === 1) {
          this.translateX = 0;
          this.translateY = 0;
        }
      }
    },
    resetZoom() {
      this.scale = 1;
      this.translateX = 0;
      this.translateY = 0;
    },
    // Touch handlers for mobile
    handleTouchStart(event) {
      if (!this.showThumbnails && event.touches.length === 2) {
        // Pinch zoom
        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        this.touchStartDistance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );
        this.initialScale = this.scale;
      } else if (!this.showThumbnails && event.touches.length === 1 && this.scale > 1) {
        // Pan
        this.lastTouchX = event.touches[0].clientX;
        this.lastTouchY = event.touches[0].clientY;
      }
    },
    handleTouchMove(event) {
      if (!this.showThumbnails && event.touches.length === 2) {
        event.preventDefault();
        const touch1 = event.touches[0];
        const touch2 = event.touches[1];
        const currentDistance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );
        
        const scaleChange = currentDistance / this.touchStartDistance;
        this.scale = Math.max(1, Math.min(5, this.initialScale * scaleChange));
        
        if (this.scale === 1) {
          this.translateX = 0;
          this.translateY = 0;
        }
      } else if (!this.showThumbnails && event.touches.length === 1 && this.scale > 1) {
        event.preventDefault();
        const deltaX = event.touches[0].clientX - this.lastTouchX;
        const deltaY = event.touches[0].clientY - this.lastTouchY;
        
        this.translateX += deltaX / this.scale;
        this.translateY += deltaY / this.scale;
        
        this.lastTouchX = event.touches[0].clientX;
        this.lastTouchY = event.touches[0].clientY;
      }
    },
    handleTouchEnd() {
      if (this.scale === 1) {
        this.translateX = 0;
        this.translateY = 0;
      }
    }
  },
  mounted() {
    // Keyboard support - ESC to close
    this.handleKeydown = (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        this.closeViewer();
      }
    };
    document.addEventListener('keydown', this.handleKeydown);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown);
  }
}
</script>

<style scoped>
.image-viewer {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  background: #000000;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  box-sizing: border-box;
}

.close-button {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #ffffff;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10001;
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.2);
}

.main-image-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
}

.main-image {
  min-width: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  user-select: none;
  -webkit-user-select: none;
  touch-action: none;
}

.thumbnails-container {
  position: absolute;
  bottom: 30px;
  left: 50%;
  margin-bottom: 120px;
  transform: translateX(-50%);
  display: flex;
  gap: 12px;
  padding: 16px 24px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 10000;
}

.thumbnail {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  opacity: 0.6;
}

.thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail:hover {
  opacity: 1;
}

.thumbnail.active {
  border-color: #00d4ff;
  opacity: 1;
  box-shadow: 0 0 12px rgba(0, 212, 255, 0.6);
}

@media (max-width: 768px) {
  .close-button {
    top: 16px;
    right: 16px;
    width: 40px;
    height: 40px;
  }

  .close-button svg {
    width: 20px;
    height: 20px;
  }

  .thumbnails-container {
    bottom: 20px;
    gap: 8px;
    padding: 12px 16px;
  }

  .thumbnail {
    width: 50px;
    height: 50px;
  }

  .main-image {
    max-width: 95%;
    max-height: 95%;
  }
}
</style>